import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import {
    Box, Text, Input,
    FormControl, FormLabel, Select,
} from '@chakra-ui/react';
import { AppDialog, DialogButton } from '../common/AppDialog';
import { pickFileToSave, writeHandleText, downloadTextFile } from '../../services/fileSystem/fileAccess';
import { writeDxfNative } from '../../services/cad/dxf/writeDxfNative';
import { DXF_VERSION_CHOICES, DEFAULT_DXF_VERSION, type DxfVersion } from '../../services/cad/dxf/types';
import { DXF_EXPORT_DIALOG_EVENT, type DxfExportRequest } from '../../services/cad/dxf/exportDialogBridge';
import { notify } from '../../utils/notify';
import i18n from '../../i18n';

const DXF_FILE_TYPES = [
    { description: 'AutoCAD Drawing Exchange', accept: { 'application/dxf': ['.dxf'] } },
];

interface DxfExportDialogProps {
    isOpen: boolean;
    request: DxfExportRequest | null;
    onClose: () => void;
}

/**
 * DXF export dialog: native version target picker (DXF 2018 … R14, §3.2
 * decision 13) + the OS save dialog (File System Access API, download
 * fallback). Serves both the whole-drawing EXPORT flow and the WBLOCK
 * subset flow (the request carries the dependency-closed entity/blocks/
 * layers subset).
 */
export function DxfExportDialog({ isOpen, request, onClose }: DxfExportDialogProps) {
    const { t } = useTranslation();
    const [fileName, setFileName] = useState('export');
    const [version, setVersion] = useState<DxfVersion>(DEFAULT_DXF_VERSION);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setFileName(request?.fileName ? request.fileName : 'export');
        setVersion(DEFAULT_DXF_VERSION);
    }, [isOpen, request]);

    const handleExport = async () => {
        const text = writeDxfNative(request?.entities, {
            version,
            blocks: request?.blocks,
            layers: request?.layers,
        });
        const safeName = (fileName.trim() || 'export').replace(/\.dxf$/i, '');
        const full = `${safeName}.dxf`;
        setBusy(true);
        try {
            const handle = await pickFileToSave(full, DXF_FILE_TYPES);
            if (handle) {
                await writeHandleText(handle, text);
                notify(i18n.t('tools.notify.dxfExported', { fileName: handle.name }), 'success');
            } else {
                downloadTextFile(full, text, 'application/dxf');
                notify(i18n.t('tools.notify.dxfExported', { fileName: full }), 'success');
            }
            onClose();
        } catch (error) {
            console.error('[DxfExportDialog] export failed:', error);
            notify(i18n.t('tools.notify.dxfExportFailed', { fileName: full }), 'error');
        } finally {
            setBusy(false);
        }
    };

    return (
        <AppDialog
            isOpen={isOpen}
            onClose={onClose}
            size="md"
            title={t('dialogs.ui.exportDxfTitle')}
            footer={
                <>
                    <DialogButton variant="ghost" onClick={onClose}>{t('dialogs.ui.cancel')}</DialogButton>
                    <DialogButton variant="primary" isDisabled={busy} onClick={() => void handleExport()}>
                        {t('dialogs.ui.dxfExport')}
                    </DialogButton>
                </>
            }
        >
            <Text fontSize="xs" color="text.secondary" mb={3}>{t('dialogs.ui.exportDxfDescription')}</Text>
            <FormControl mb={3} isRequired>
                <FormLabel fontSize="xs">{t('dialogs.ui.dxfFileName')}</FormLabel>
                <Input
                    size="sm"
                    value={fileName}
                    onChange={(e) => setFileName(e.target.value)}
                    placeholder="export.dxf"
                />
            </FormControl>
            <FormControl mb={1}>
                <FormLabel fontSize="xs">{t('dialogs.ui.dxfVersion')}</FormLabel>
                <Select
                    size="sm"
                    value={version}
                    onChange={(e) => setVersion(e.target.value as DxfVersion)}
                >
                    {DXF_VERSION_CHOICES.map((v) => (
                        <option key={v.version} value={v.version}>
                            {`DXF ${v.release} (${v.version})`}
                        </option>
                    ))}
                </Select>
            </FormControl>
            <Box minH="2" />
        </AppDialog>
    );
}

/** Lazy-mount gate shared with ProjectDialogsHost. */
export function isDxfExportEvent(e: Event): e is CustomEvent<DxfExportRequest> {
    return e.type === DXF_EXPORT_DIALOG_EVENT;
}
